import React, {useMemo} from 'react';
import {useNavigate} from 'react-router';
import styles from './ConsentsDocumentsTable.module.scss';
import {TableColumnConfig, Text, Spin, Checkbox, Icon} from '@gravity-ui/uikit';
import {DataTable} from '../../shared';
import {ConsentsDocumentItemModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {FileCheck, Archive} from '@gravity-ui/icons';
import classNames from 'classnames';

interface ConsentsDocumentsTableProps {
  documents: ConsentsDocumentItemModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const ConsentsDocumentsTable = ({
  documents,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ConsentsDocumentsTableProps) => {
  const navigate = useNavigate();

  const handleRowClick = (document: ConsentsDocumentItemModel) => {
    navigate(`/administration/consents-documents/${document.id}`);
  };
  const columns = useMemo<TableColumnConfig<ConsentsDocumentItemModel>[]>(
    () => [
      {
        id: 'title',
        name: 'Название',
        width: 300,
        template: (item) => (
          <div>
            <Icon
              size={16}
              data={!item.isArchived ? FileCheck : Archive}
              className={classNames(styles.fileIcon, {[styles.archivedIcon]: item.isArchived})}
            />{' '}
            <Text variant="body-1" className={styles.titleText}>
              {item.title}
            </Text>
          </div>
        ),
      },
      {
        id: 'isDistinguished',
        name: 'Отдельно',
        width: 150,
        template: (item) => <Checkbox checked={item.isDistinguished} />,
      },
      {
        id: 'isMandatory',
        name: 'Обязательный',
        width: 150,
        template: (item) => <Checkbox checked={item.isMandatory} />,
      },
      {
        id: 'numberOfVersions',
        name: 'Кол-во-версий',
        width: 150,
        template: (item) => <Text variant="body-1">{item.numberOfVersions}</Text>,
      },
      {
        id: 'order',
        name: 'Порядок',
        width: 100,
        sortable: false,
        template: (item) => <Text variant="body-1">{item.order}</Text>,
      },
    ],
    [],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable
        data={documents}
        columns={columns}
        isLoading={isLoading}
        emptyStateTitle="Документы согласия не найдены"
        emptyStateDescription="Попробуйте изменить фильтры или добавьте новые документы согласия."
        onRowClick={handleRowClick}
      />
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
          {isFetchingNextPage && (
            <div className={styles.loadingSpinnerContainer}>
              <Spin size="l" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
